import { Box, Group, SimpleGrid } from '@mantine/core';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ModelComparisonSummary } from '../../../api/agent-miner-client/agent-miner-client.type';
import { SingleValueCard } from '../../../components/overview-card/single-value-card';
import { inkLight } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { fmtMetric } from './model-comparison.helpers';

type Props = {
  summary: ModelComparisonSummary | undefined;
  isLoading: boolean;
};

/**
 * Tile title with an info icon whose tooltip clarifies the metric's scope —
 * used for the three tiles computed only from tasks linked to an agentic
 * session, so it reads distinctly from an org-wide figure.
 *
 * @param {string} label - The visible tile title
 * @param {string} tooltip - The explanation shown on hover
 * @returns {ReactNode} The composed title content
 */
const titleWithInfo = (label: string, tooltip: string): ReactNode => (
  <Group gap={4} wrap="nowrap">
    {label}
    <Tooltip label={tooltip} position="top" variant="white" events={{ hover: true, focus: true, touch: false }}>
      <Box component="span" tabIndex={0} aria-label={tooltip} style={{ display: 'inline-flex', cursor: 'help' }}>
        <Icon name="info" size={13} color={inkLight} />
      </Box>
    </Tooltip>
  </Group>
);

/**
 * KPI strip for the Model Comparison tab: agentic sessions, median task cycle
 * time, task throughput, and rework rate. The latter three are scoped to
 * tasks with a confidently linked agentic session, not the org's full task
 * volume — the info tooltip on each spells that out.
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} A four-tile KPI row
 */
export const KpiStrip = ({ summary, isLoading }: Props) => {
  const { t } = useTranslation();

  return (
    <SimpleGrid cols={{ base: 2, lg: 4 }} spacing={16} data-testid="model-comparison-kpi-strip">
      <SingleValueCard
        title={t('agent_miner.model_comparison.agentic_sessions')}
        icon="terminal"
        value={summary ? summary.agentic_sessions.toLocaleString() : '—'}
        isLoading={isLoading}
      />
      <SingleValueCard
        title={titleWithInfo(
          t('agent_miner.model_comparison.median_cycle_time'),
          t('agent_miner.model_comparison.cycle_days_tooltip'),
        )}
        icon="timer"
        value={fmtMetric(summary?.median_task_cycle_time_days)}
        unit={t('agent_miner.model_comparison.median_cycle_time_unit')}
        isLoading={isLoading}
      />
      <SingleValueCard
        title={titleWithInfo(
          t('agent_miner.model_comparison.task_throughput'),
          t('agent_miner.model_comparison.task_throughput_tooltip'),
        )}
        icon="task_alt"
        value={summary ? summary.task_throughput.toLocaleString() : '—'}
        unit={t('agent_miner.model_comparison.task_throughput_unit')}
        isLoading={isLoading}
      />
      <SingleValueCard
        title={titleWithInfo(
          t('agent_miner.model_comparison.rework_rate'),
          t('agent_miner.model_comparison.rework_rate_tooltip'),
        )}
        icon="undo"
        value={fmtMetric(summary?.rework_rate_pct)}
        unit="%"
        isLoading={isLoading}
      />
    </SimpleGrid>
  );
};
